// src/pages/PatientProfilePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  PhoneIphone as PhoneIcon,
  Wc as WcIcon,
  LocationOn as LocationIcon,
  Science as BloodIcon,
  Work as WorkIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';

const SEXO_OPTIONS = [
  { value: 'Masculino', label: 'Masculino' },
  { value: 'Femenino', label: 'Femenino' },
];

const GRUPO_SANGUINEO = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const API_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
const toAbsoluteUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const path = u.startsWith('/') ? u : `/${u}`;
  return `${base}${path}`;
};

const PatientProfilePage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [serverErrors, setServerErrors] = useState({});

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    fecha_nacimiento: null,
    sexo: '',
    residencia: '',
    grupo_sanguineo: '',
    profesion: '',
    foto_perfil_url: '',
  });
  const [fotoFile, setFotoFile] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);

  const initials = useMemo(() => {
    const n = form.first_name?.trim()?.charAt(0) || '';
    const a = form.last_name?.trim()?.charAt(0) || '';
    return (n + a || 'P').toUpperCase();
  }, [form.first_name, form.last_name]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setServerErrors({});
      const { data } = await axiosInstance.get('/patients/profile/');
      setForm({
        first_name: data?.first_name || '',
        last_name: data?.last_name || '',
        email: data?.email || '',
        phone_number: data?.phone_number || '',
        fecha_nacimiento: data?.fecha_nacimiento ? new Date(data.fecha_nacimiento) : null,
        sexo: data?.sexo || '',
        residencia: data?.residencia || '',
        grupo_sanguineo: data?.grupo_sanguineo || '',
        profesion: data?.profesion || '',
        foto_perfil_url: toAbsoluteUrl(data?.foto_perfil) || '',
      });
      setFotoFile(null);
      setTimeout(() => setFadeIn(true), 100);
    } catch (e) {
      console.error(e);
      setError('No se pudo cargar tu perfil. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (serverErrors[e.target.name]) {
      setServerErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleDateChange = (value) => {
    setForm(prev => ({ ...prev, fecha_nacimiento: value }));
    if (serverErrors['fecha_nacimiento']) {
      setServerErrors(prev => ({ ...prev, fecha_nacimiento: '' }));
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.warn('La imagen es muy grande. Máximo 2MB.');
      return;
    }
    setFotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, foto_perfil_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    fetchProfile();
    setServerErrors({});
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Requerido';
    if (!form.last_name.trim()) errs.last_name = 'Requerido';
    if (!form.email.trim()) errs.email = 'Requerido';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Correo inválido';
    if (form.phone_number && !/^\+?[\d\s\-()]+$/.test(form.phone_number)) errs.phone_number = 'Teléfono inválido';
    if (!form.sexo) errs.sexo = 'Selecciona un sexo';
    if (!form.fecha_nacimiento) errs.fecha_nacimiento = 'Selecciona una fecha';
    setServerErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      setError('');
      setServerErrors({});

      const fd = new FormData();
      // Usuario
      fd.append('first_name', form.first_name);
      fd.append('last_name', form.last_name);
      fd.append('phone_number', form.phone_number || '');
      fd.append('email', (form.email || '').trim().toLowerCase());
      // Paciente
      fd.append('sexo', form.sexo);
      fd.append('residencia', form.residencia || '');
      fd.append('grupo_sanguineo', form.grupo_sanguineo || '');
      fd.append('profesion', form.profesion || '');
      if (form.fecha_nacimiento) {
        fd.append('fecha_nacimiento', form.fecha_nacimiento.toISOString().slice(0, 10));
      }
      if (fotoFile) {
        fd.append('foto_perfil', fotoFile);
      }

      // No fijes Content-Type: deja que el navegador ponga el boundary
      await axiosInstance.patch('/patients/profile/', fd);

      toast.success('Perfil actualizado correctamente');
      await fetchProfile();
    } catch (e) {
      console.error(e);
      const apiErr = e.response?.data;
      if (apiErr && typeof apiErr === 'object') {
        setServerErrors(apiErr);
      } else {
        setError('No se pudo guardar el perfil. Intenta nuevamente.');
        toast.error('Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const paperStyle = {
    p: 3,
    borderRadius: '20px',
    boxShadow: 'none',
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={paperStyle}>
        {/* Cabecera tipo MedicoProfilePage */}
        <Fade in={fadeIn} timeout={700}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={form.foto_perfil_url || undefined}
                alt={`${form.first_name} ${form.last_name}`}
                sx={{
                  width: 110,
                  height: 110,
                  bgcolor: theme.palette.primary.main,
                  fontSize: 36,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                }}
              >
                {(!form.foto_perfil_url) ? initials : null}
              </Avatar>
              <IconButton
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: -6,
                  right: -6,
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  boxShadow: theme.shadows[3],
                }}
                aria-label="Subir foto de perfil"
              >
                <PhotoCamera />
                <input hidden accept="image/*" type="file" onChange={handleFotoChange} />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Mi Perfil
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona tu información personal y de contacto
              </Typography>
            </Box>

            {/* Acciones SOLO aquí (se elimina duplicación) */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ResetIcon />} // evitamos type error en TS; ignóralo si usas JS
                onClick={resetForm}
                disabled={saving}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Restablecer
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          </Box>
        </Fade>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Sección: Información personal */}
        <Fade in={fadeIn} timeout={900}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <PersonIcon />
              </Avatar>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Información Personal
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombres"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!serverErrors.first_name}
                  helperText={serverErrors.first_name || ' '}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Apellidos"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!serverErrors.last_name}
                  helperText={serverErrors.last_name || ' '}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Correo electrónico"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  error={!!serverErrors.email}
                  helperText={serverErrors.email || ' '}
                  InputProps={{
                    startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChange}
                  fullWidth
                  error={!!serverErrors.phone_number}
                  helperText={serverErrors.phone_number || ' '}
                  InputProps={{
                    startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Profesión"
                  name="profesion"
                  value={form.profesion}
                  onChange={handleChange}
                  fullWidth
                  error={!!serverErrors.profesion}
                  helperText={serverErrors.profesion || ' '}
                  InputProps={{
                    startAdornment: <WorkIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de nacimiento"
                  value={form.fecha_nacimiento}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!serverErrors.fecha_nacimiento,
                      helperText: serverErrors.fecha_nacimiento || ' ',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!serverErrors.sexo}>
                  <InputLabel id="sexo-label">Sexo</InputLabel>
                  <Select
                    labelId="sexo-label"
                    label="Sexo"
                    name="sexo"
                    value={form.sexo}
                    onChange={handleChange}
                    startAdornment={<WcIcon color="action" sx={{ mr: 1 }} />}
                  >
                    {SEXO_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{serverErrors.sexo || ' '}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Sección: Contacto y salud */}
        <Fade in={fadeIn} timeout={1100}>
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                }}
              >
                <LocationIcon />
              </Avatar>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Contacto y Datos de Salud
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Dirección/Residencia"
                  name="residencia"
                  value={form.residencia}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                  error={!!serverErrors.residencia}
                  helperText={serverErrors.residencia || ' '}
                  InputProps={{
                    startAdornment: <LocationIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!serverErrors.grupo_sanguineo}>
                  <InputLabel id="gsangre-label">Grupo sanguíneo</InputLabel>
                  <Select
                    labelId="gsangre-label"
                    label="Grupo sanguíneo"
                    name="grupo_sanguineo"
                    value={form.grupo_sanguineo}
                    onChange={handleChange}
                    startAdornment={<BloodIcon color="action" sx={{ mr: 1 }} />}
                  >
                    {GRUPO_SANGUINEO.map((gs) => (
                      <MenuItem key={gs} value={gs}>
                        <Chip size="small" label={gs} color="primary" />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{serverErrors.grupo_sanguineo || ' '}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Paper>
    </Container>
  );
};

export default PatientProfilePage;
